import React, { useEffect, useState } from "react";
import ChantCheckbox from "../../../../../CommonComponents/ChantCheckbox/ChantCheckbox";
import { InputNumber, Button } from "antd";
import dayjs from "dayjs";
import { randomString } from "../../../../../helperFunctions/util";
import "./EventPoints.css";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import HelpTooltip from "../../../../../CommonComponents/HelpTooltip";
import { ChantInput, ChantSelect } from "../../../../../CommonComponents";
import ChantTimePicker from "../../../../../CommonComponents/ChantTimePicker/ChantTimePicker";

const EventPoints = ({
  offerPoints,
  validateGeolocation,
  eventStartDate,
  viewMode,
  currentSeason,
  selectedMatchSeason,
  volunteerSlots,
  checkinPoints,
  maxRsvp,
  onSeasonChange,
  onPointsChange,
  onMaxRsvpChange,
  onVolunteerRequestsChange,
  onOfferPointsChange,
  onCheckLocationChange,
}) => {
  const currentYear = dayjs().year();

  const seasons = [
    { key: currentYear, value: currentYear },
    { key: currentYear - 1, value: currentYear - 1 },
  ];

  const [season, setSeason] = useState(selectedMatchSeason || currentSeason);
  const [points, setPoints] = useState(checkinPoints || 0);
  const [volunteerRequests, setVolunteerRequests] = useState(
    volunteerSlots || [],
  );
  const [timePickers, setTimePickers] = useState({});

  useEffect(() => {
    if (selectedMatchSeason) {
      setSeason(selectedMatchSeason);
    }
  }, [selectedMatchSeason]);

  useEffect(() => {
    const initialTimePickers = {};
    volunteerRequests.forEach((request, index) => {
      initialTimePickers[`${index}-start`] = request?.startTime
        ? dayjs.unix(request.startTime)
        : null;
      initialTimePickers[`${index}-end`] = request?.endTime
        ? dayjs.unix(request.endTime)
        : null;
    });
    setTimePickers(initialTimePickers);
  }, [volunteerRequests]);

  const timeFormat = "HH:mm";
  const VolunteerTemplate = {
    sortIndex: 0,
    title: "",
    description: "",
    slotCount: 0,
    startTime: undefined,
    endTime: undefined,
    points: 0,
  };

  const handleDeleteVolunteer = (requestId) => {
    setVolunteerRequests((prev) => {
      const newRequests = prev.filter((req) => req.id !== requestId);
      newRequests.forEach((req, index) => {
        req.sortIndex = index;
      });
      onVolunteerRequestsChange(newRequests);
      return newRequests;
    });
  };

  const handleVolunteerFieldChange = (index, field, value) => {
    setVolunteerRequests((prev) => {
      const newRequests = [...prev];
      newRequests[index] = {
        ...newRequests[index],
        [field]: value,
      };
      onVolunteerRequestsChange(newRequests);
      return newRequests;
    });
  };

  const handleTimeChange = (index, field, value) => {
    if (!value) {
      setVolunteerRequests((prev) => {
        const newRequests = [...prev];
        newRequests[index] = {
          ...newRequests[index],
          [field]: null,
        };
        onVolunteerRequestsChange(newRequests);
        return newRequests;
      });
      setTimePickers((prev) => ({
        ...prev,
        [`${index}-${field}`]: null,
      }));
      return;
    }

    const hh = value.hour();
    const mm = value.minute();
    const epochTime = dayjs.unix(eventStartDate).hour(hh).minute(mm).unix();

    setVolunteerRequests((prev) => {
      const newRequests = [...prev];
      newRequests[index] = {
        ...newRequests[index],
        [field]: epochTime,
      };
      onVolunteerRequestsChange(newRequests);
      return newRequests;
    });

    setTimePickers((prev) => ({
      ...prev,
      [`${index}-${field}`]: value,
    }));
  };

  return (
    <div className="event-points-container">
      <h5 className="event-points-title">
        Event RSVPs, Check-in &amp; Volunteers
      </h5>
      <p className="event-points-description">
        Offer event points &amp; manage volunteers with members added to the
        Events Leaderboard. If geolocation will be used you must add a
        &apos;Location&apos; above. Season is required. Non-member (Fan) role is
        not eligible for points.
      </p>

      {/* ── Section 1: Set Maximum RSVPs ── */}
      <div className="ep-section-card">
        <div className="ep-section-header">Set Maximum RSVPs</div>
        <div className="ep-section-body">
          <div className="event-points-field-row">
            <div className="event-points-input-wrapper">
              <ChantInput
                type="number"
                min={0}
                max={99999}
                value={maxRsvp ?? ""}
                onChange={(e) => {
                  // Whole numbers only, at most 5 digits
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 5);
                  onMaxRsvpChange(digits !== "" ? Number(digits) : null);
                }}
                disabled={viewMode}
                placeholder="No maximum"
              />
            </div>
            <div className="event-points-help-text-container">
              <p className="event-points-help-text">
                (Optional) Set maximum number RSVPs allowed for the event.
                Separately you can control event attendance by creating a
                ticket.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: Check-in Setup ── */}
      <div className="ep-section-card">
        <div className="ep-section-header">Check-in Setup</div>
        <div className="ep-section-body">
          <div className="event-points-field-row">
            <div className="event-points-field-control">
              <ChantCheckbox
                checked={offerPoints}
                onChange={(e) => onOfferPointsChange(e.target.checked)}
              >
                <span className="event-points-checkbox-label">
                  Offer points upon check-in?
                </span>
              </ChantCheckbox>
              <HelpTooltip text="Check-in button added to event card on event date. Requires member be present to check-in. Must set address (above) using 'Add Google Maps Link'." />
            </div>
            <p className="event-points-help-text">
              If not enabled, members can check-in but with no points awarded.
            </p>
          </div>

          {offerPoints && (
            <div className="event-points-field-row event-points-field-row-spaced">
              <div className="event-points-field-control">
                <ChantCheckbox
                  checked={validateGeolocation}
                  onChange={(e) => onCheckLocationChange(e.target.checked)}
                >
                  <span className="event-points-checkbox-label">
                    Check members location?
                  </span>
                </ChantCheckbox>
              </div>
              <p className="event-points-help-text">
                Geolocation used by default. Disable for virtual events.
              </p>
            </div>
          )}

          {offerPoints && (
            <div style={{ marginTop: 16 }}>
              <ChantSelect
                helpTooltip={
                  <HelpTooltip text="If no match selected for event, set 'season' to properly assign points." />
                }
                style={{ width: "150px" }}
                label="Season"
                placeholder="Select season"
                options={seasons}
                value={season}
                onChange={(value) => {
                  setSeason(value);
                  onSeasonChange(value);
                }}
                disabled={selectedMatchSeason}
              />
              <p
                className="event-points-help-text"
                style={{ marginTop: 8, maxWidth: 480 }}
              >
                Selecting a match (above) will automatically set the season. If
                no match selected, use the first year of your season (2025 for
                2025-26). Warning: Changing the season might start a new
                leaderboard.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 3: Event Points (conditional) ── */}
      {offerPoints && (
        <div className="ep-section-card">
          <div className="ep-section-header">Event Points</div>
          <div className="ep-section-body">
            <div className="event-points-field-row">
              <div className="event-points-input-wrapper">
                <InputNumber
                  min={0}
                  max={1000}
                  defaultValue={0}
                  value={String(points) || "0"}
                  onChange={(value) => {
                    setPoints(value);
                    onPointsChange(value);
                  }}
                  onWheel={(e) => e.target.blur()}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="event-points-help-text-container">
                <p className="event-points-help-text">
                  Examples: Watch Party (100), Travel (500). Note: Volunteer
                  points can be assigned by the event here or by the volunteer
                  slot (below).
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 4: Request Volunteers (conditional) ── */}
      {offerPoints && (
        <div className="ep-section-card">
          <div className="ep-section-header">Request Volunteers</div>
          <div className="ep-section-body">
            <p className="event-points-section-description">
              Use this section to request volunteers for specific slots. Another
              option is to use the &apos;Volunteer&apos; event type and offer
              general event points (all volunteers granted the same points upon
              check-in).
            </p>

            {volunteerRequests
              .sort((a, b) => a?.sortIndex - b?.sortIndex)
              .map((request, index) => (
                <div key={request?.id} className="volunteer-slot-container">
                  <div className="volunteer-slot-header">
                    <h6 className="volunteer-slot-title">Slot {index + 1}</h6>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteVolunteer(request.id)}
                      className="volunteer-slot-delete-btn"
                    >
                      Delete
                    </Button>
                  </div>

                  <div className="volunteer-slot-divider" />

                  <div className="volunteer-slot-fields">
                    <div className="volunteer-slot-field">
                      <ChantInput
                        label={
                          <span>
                            Title of slot{" "}
                            <span className="required-asterisk">*</span>
                          </span>
                        }
                        value={request.title}
                        maxLength={50}
                        onChange={(e) =>
                          handleVolunteerFieldChange(
                            index,
                            "title",
                            e.target.value,
                          )
                        }
                        placeholder="Max 50 Characters"
                        disabled={viewMode}
                      />
                    </div>

                    <div className="volunteer-slot-field">
                      <ChantInput
                        label="Description"
                        value={request.description}
                        maxLength={100}
                        onChange={(e) =>
                          handleVolunteerFieldChange(
                            index,
                            "description",
                            e.target.value,
                          )
                        }
                        placeholder="Max 100 Characters"
                        disabled={viewMode}
                      />
                    </div>

                    <div className="volunteer-slot-field">
                      <label className="volunteer-slot-field-label">
                        # of slots <span className="required-asterisk">*</span>
                      </label>
                      <InputNumber
                        value={String(request.slotCount) || "0"}
                        min={0}
                        max={999}
                        maxLength={3}
                        onChange={(value) =>
                          handleVolunteerFieldChange(
                            index,
                            "slotCount",
                            String(value),
                          )
                        }
                        disabled={viewMode}
                        style={{ width: "100%" }}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>

                    <div className="volunteer-slot-field">
                      <label className="volunteer-slot-field-label">
                        Start / End Time
                      </label>
                      <div className="volunteer-slot-time-pickers">
                        <ChantTimePicker
                          value={timePickers[`${index}-start`]}
                          defaultOpenValue={dayjs().hour(0).minute(0).second(0)}
                          onChange={(value) =>
                            handleTimeChange(index, "startTime", value)
                          }
                          format={timeFormat}
                          disabled={viewMode}
                          style={{ flex: "1 1 200px" }}
                        />
                        <ChantTimePicker
                          value={timePickers[`${index}-end`]}
                          defaultOpenValue={dayjs()
                            .set("hour", 0)
                            .set("minute", 0)}
                          onChange={(value) =>
                            handleTimeChange(index, "endTime", value)
                          }
                          format={timeFormat}
                          disabled={viewMode}
                          style={{ flex: "1 1 200px" }}
                        />
                      </div>
                    </div>

                    <div className="volunteer-slot-field">
                      <label className="volunteer-slot-field-label">
                        Offer volunteer points?
                      </label>
                      <InputNumber
                        value={String(request?.points) || "0"}
                        min={0}
                        max={9999}
                        maxLength={4}
                        onChange={(value) =>
                          handleVolunteerFieldChange(
                            index,
                            "points",
                            String(value),
                          )
                        }
                        disabled={viewMode}
                        style={{ width: "100%" }}
                        onWheel={(e) => e.target.blur()}
                      />
                      <span className="volunteer-slot-field-hint">
                        Offered to volunteers in addition to &apos;event
                        points&apos; (above)
                      </span>
                    </div>
                  </div>
                </div>
              ))}

            <div className="volunteer-slot-add-container">
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  setVolunteerRequests((prev) => {
                    const newRequests = [
                      ...prev,
                      {
                        ...VolunteerTemplate,
                        id: randomString(),
                        sortIndex: prev.length,
                      },
                    ];
                    onVolunteerRequestsChange(newRequests);
                    return newRequests;
                  });
                }}
                className="volunteer-slot-add-btn"
              >
                Add volunteer slot
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventPoints;
